import { DocumentOrganizationKeys, FilterDocument } from '@griply/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, OpenCollapse } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { IconButton } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useTree } from 'shared/components/ui/virtualized-tree-list';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

export type ExtraOptionsMenuProps = {
  filterDocument: FilterDocument;
  updateFilterDocument: (filterDocument: FilterDocument) => void;
};

export const ExtraOptionsMenu: React.FC<ExtraOptionsMenuProps> = ({
  filterDocument,
  updateFilterDocument,
}) => {
  const { t } = useTranslation();
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const [tree] = useTree();

  const toggleGroups = () => {
    if (filterDocument[DocumentOrganizationKeys.CollapsedSections]?.length) {
      updateFilterDocument({
        ...filterDocument,
        [DocumentOrganizationKeys.CollapsedSections]: null,
      });

      closeMenu();
      return;
    }

    const items = tree?.getItems();
    if (!items?.length) {
      return;
    }

    const collapsedSections = items.filter(
      (item) => item.getItemData().isFolder,
    );

    updateFilterDocument({
      ...filterDocument,
      [DocumentOrganizationKeys.CollapsedSections]: collapsedSections.map(
        (item) => item.getItemData().sourceId,
      ),
    });

    closeMenu();
  };

  const toggleShowCompletedTasks = () => {
    updateFilterDocument({
      ...filterDocument,
      showCompleted: !filterDocument.showCompleted,
    });
    closeMenu();
  };

  return (
    <>
      <IconButton
        icon={Filter}
        ref={filterButtonRef}
        onClick={openMenu}
        preventDrag
      />
      {menuOpen && (
        <PopupMenu
          referenceElement={filterButtonRef}
          onOutside={closeMenu}
          position="bottom-end"
        >
          <PopupMenuList hasBottomBorder>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={toggleGroups}
                start={<Icon icon={OpenCollapse} />}
              >
                {t('pages.all-tasks.expand-collapse-groups.label')}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
          <PopupMenuList>
            <PopupMenuListItem>
              <PopupMenuToggle
                label={t('pages.all-tasks.show-completed.label')}
                onChange={toggleShowCompletedTasks}
                checked={!!filterDocument.showCompleted}
              />
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
